import React from "react";
import {
  DialogTitle,
  Select,
  MenuItem,
  Typography,
  Box,
  Tooltip,
  Button,
  Avatar,
  Stack,
  Chip,
} from "@mui/material";
import luxor from "../../../assets/luxor.png";
import LockIcon from "@mui/icons-material/Lock";
import InputIcon from "@mui/icons-material/Input";
import CheckIcon from "@mui/icons-material/Check";
import { useDiscussionCaseContext } from "@/contexts/DiscussionCaseContext";
import { useMedicalReportsAgentContext } from "@/contexts/MedicalReportsAgentContext";
import { useGuidanceAgentContext } from "@/contexts/GuidanceAgentContext";
import { LuxorHeaderProps } from "../types";
import { contractTypeMapping, isTypeAvailable, hasAnyActiveContract } from "../isTypeAvailable";
import { getProviderDisplayForLuxorType } from "../luxorProviderUtils";

export const LuxorHeader: React.FC<LuxorHeaderProps> = ({
  selectedType,
  endpointConfigs,
  onTypeChange,
  onImport,
  imported,
  showImportButton = true,
}) => {
  const { contractDiscussionCase, provider: discussionCaseProvider } =
    useDiscussionCaseContext();
  const { contractMedicalReports, provider: medicalReportsProvider } =
    useMedicalReportsAgentContext();
  const { contractGuidance, provider: guidanceProvider } =
    useGuidanceAgentContext();

  const selectedTypeHasContract = isTypeAvailable(
    selectedType,
    contractDiscussionCase,
    contractMedicalReports,
    contractGuidance
  );

  const providerLabel = getProviderDisplayForLuxorType(selectedType, {
    medicalReportsProvider,
    discussionCaseProvider,
    guidanceProvider,
  });

  return (
    <DialogTitle
      sx={{
        border: "1px solid",
        borderColor: "primary.main",
        color: "white",
        display: "flex",
        flexDirection: { xs: "column", sm: "row" },
        justifyContent: "space-between",
        alignItems: { xs: "stretch", sm: "center" },
        gap: 1,
        py: 1,
        px: 2,
        marginBottom: 1,
        borderTopLeftRadius: 12,
        borderTopRightRadius: 12,
      }}
    >
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Avatar src={luxor} alt="Luxor" sx={{ width: 36, height: 36 }} />
        <Typography
          variant="h6"
          fontWeight={700}
          letterSpacing={0.5}
          color="primary.main"
        >
          LuxorMed
        </Typography>
        {selectedTypeHasContract && providerLabel !== "-" && (
          <Chip
            label={`Provedor: ${providerLabel}`}
            size="small"
            variant="outlined"
            color="primary"
            sx={{ fontSize: "0.75rem", height: 24 }}
          />
        )}
      </Box>

      <Box
        display="flex"
        alignItems="center"
        gap={1}
        flexWrap="wrap"
        justifyContent={{ xs: "flex-start", sm: "flex-end" }}
      >
        {showImportButton && onImport && (
          <Tooltip
            title="Preenche o campo abaixo com anamnese, exames, condutas e demais informações já registradas neste atendimento"
            placement="bottom"
          >
            <Button
              startIcon={imported ? <CheckIcon /> : <InputIcon />}
              size="small"
              variant="outlined"
              onClick={onImport}
              sx={{
                fontSize: "0.75rem",
                textTransform: "none",
                borderRadius: 2,
                border: "1px solid",
                borderColor: "primary.main",
                color: "primary.main",
                whiteSpace: "nowrap",
                "&:hover": {
                  backgroundColor: "rgba(0, 174, 154, 0.1)",
                },
              }}
            >
              {imported ? "Importado!" : "Importar dados do atendimento"}
            </Button>
          </Tooltip>
        )}

        <Select
          value={selectedType}
          onChange={onTypeChange}
          size="small"
          variant="outlined"
          disabled={
            !hasAnyActiveContract(
              contractDiscussionCase,
              contractMedicalReports,
              contractGuidance
            )
          }
          sx={{
            bgcolor: "whitesmoke",
            minWidth: 180,
            borderRadius: 2,
            boxShadow: "0px 2px 4px rgba(0, 0, 0, 0.05)",
            "& .MuiSelect-select": {
              py: 1,
            },
            "& .MuiOutlinedInput-notchedOutline": {
              border: "none",
            },
            "&:hover": {
              bgcolor: "grey.50",
            },
          }}
          inputProps={{ "aria-label": "Tipo de análise" }}
        >
          {Object.entries(endpointConfigs).map(([key, config]) => {
            const isAvailable = isTypeAvailable(
              key,
              contractDiscussionCase,
              contractMedicalReports,
              contractGuidance
            );
            const contractType = contractTypeMapping[key];
            let tooltipText = config.description || config.name;
            if (!isAvailable) {
              if (contractType === "discussion") {
                tooltipText = "Contrato de Discussão de Casos não ativo";
              } else if (contractType === "medical-reports") {
                tooltipText = "Contrato de Relatório Médico não ativo";
              } else if (contractType === "guidance") {
                tooltipText = "Contrato de Orientação ao Paciente não ativo";
              }
            }
            return (
              <MenuItem key={key} value={key}>
                <Tooltip title={tooltipText} placement="right">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    width="100%"
                  >
                    <Typography flex={1}>{config.name}</Typography>
                    {!isAvailable && (
                      <LockIcon fontSize="small" color="disabled" />
                    )}
                  </Stack>
                </Tooltip>
              </MenuItem>
            );
          })}
        </Select>
      </Box>
    </DialogTitle>
  );
};
